import * as THREE from 'three';
import { fogAdopted } from '../environment/fog-of-war.js';

/**
 * Game figures share their palette surfaces' materials, and the depth materials their shadows draw with, with every other figure.
 * Three re-sends all of a material's uniforms whenever consecutive draws switch material, and each figure building its own copies
 * made nearly every figure draw a switch: in `goblin-battle` that was about a third of the CPU spent drawing. A palette surface keeps
 * its colours and finish in its vertices and its per-figure cloth data in the bone texture (`figure-texels.ts`), so two figures'
 * materials differ only where their settings do.
 *
 * A shared material belongs to no figure: disposing it does nothing. Code that changes one figure's surfaces (a disconnected hero's
 * fade, a dissolve) first takes copies of its own with `ownFigureMaterials`, and may hand them back with `shareFigureMaterials`.
 */
const shared = new Map<string, THREE.Material>();
const rigid = new WeakMap<THREE.Material, THREE.Material>();
const SLOTS = ['material', 'customDepthMaterial', 'customDistanceMaterial'] as const;
type Slot = typeof SLOTS[number];

const keep = () => {};

/** Everything three reads from a material, or undefined when it holds something a figure owns (a texture, an unknown object). */
function signature(material: THREE.Material, skinned: boolean, slot: Slot) {
  // Palette surfaces, and plain surfaces with no shader hooks of their own (a figure's lone leather or metal parts).
  if (slot === 'material' ? !(material instanceof THREE.MeshStandardMaterial) || material instanceof THREE.MeshPhysicalMaterial
    || material.name !== 'character-palette' && Object.hasOwn(material, 'onBeforeCompile')
    : !(material instanceof THREE.MeshDepthMaterial || material instanceof THREE.MeshDistanceMaterial)) return undefined;
  const fields: unknown[] = [slot, skinned, material.type, material.customProgramCacheKey()];
  for (const [name, value] of Object.entries(material)) {
    if (name.startsWith('_') || name === 'uuid' || name === 'id' || name === 'version' || typeof value === 'function') continue;
    if (value instanceof THREE.Texture) return undefined;
    if (value instanceof THREE.Color) fields.push(name, value.getHexString());
    else if (value instanceof THREE.Vector2 || value instanceof THREE.Vector3 || value instanceof THREE.Euler) fields.push(name, value.toArray());
    else if (value === null || typeof value !== 'object') fields.push(name, value);
    // A copy's fog opt-out and the shared mark say nothing about how it draws.
    else if (name === 'userData') fields.push(name, JSON.stringify({ ...value, noFogOfWar: undefined, nimblezShared: undefined }));
    else if (name === 'defines') fields.push(name, JSON.stringify(value));
    else return undefined;
  }
  return JSON.stringify(fields);
}

/** A copy that keeps the shader hooks (`copy` drops them, and JSON-copies userData, which would cut shared uniform holders loose). */
function duplicate(source: THREE.Material) {
  const userData = source.userData; source.userData = {};
  const copy = source.clone(); source.userData = userData; copy.userData = { ...userData };
  copy.onBeforeCompile = source.onBeforeCompile; copy.customProgramCacheKey = source.customProgramCacheKey;
  // The hook already carries the fog of war's patch, which must not be applied twice.
  if (fogAdopted(source)) copy.userData.noFogOfWar = true;
  delete copy.userData.nimblezShared;
  return copy;
}

/** Swaps a figure's palette and depth materials for the shared ones with the same settings. */
export function shareFigureMaterials(root: THREE.Object3D) {
  root.traverse(object => {
    if (!(object instanceof THREE.Mesh)) return;
    for (const slot of SLOTS) {
      const material = object[slot] as THREE.Material | THREE.Material[] | undefined;
      if (!material || Array.isArray(material) || material.userData.nimblezShared) continue;
      const key = signature(material, object instanceof THREE.SkinnedMesh, slot);
      if (key === undefined) continue;
      let common = shared.get(key);
      if (!common) { common = material; common.userData.nimblezShared = key; common.dispose = keep; shared.set(key, common); }
      object[slot] = common;
    }
  });
}

/** Gives a figure copies of its own of every shared material it draws, before something changes them for it alone. */
export function ownFigureMaterials(root: THREE.Object3D) {
  root.traverse(object => {
    if (!(object instanceof THREE.Mesh)) return;
    for (const slot of SLOTS) {
      const material = object[slot] as THREE.Material | THREE.Material[] | undefined;
      if (!material || Array.isArray(material) || !material.userData.nimblezShared) continue;
      object[slot] = duplicate(material);
    }
  });
}

/**
 * The material a surface drawn with `material` uses once baked into rigid geometry (a corpse at rest): a shared material gets one rigid
 * twin, shared too, since a material drawn by both skinned and rigid meshes makes three pick its program again at every switch.
 */
export function rigidFigureMaterial<T extends THREE.Material | undefined>(material: T): T {
  if (!material?.userData.nimblezShared) return material;
  let twin = rigid.get(material);
  if (!twin) { twin = duplicate(material); twin.dispose = keep; rigid.set(material, twin); }
  return twin as T;
}
